// ContentPlanPage.tsx — Content Plan (Structured mini-app)
//
// A clean, zoomed-out, Excel-like TABLE of high-level content items. The
// table is the default (and primary) view. Rows are grouped into category
// section bands (category by category_order; rows by sort_order). The three
// meta columns — psychological angle, marketing angle, why (rationale) —
// are the point and get the room; long cells truncate with an expand toggle.
//
// Columns: Title (+ description sub-line) | Type | Funnel | Psychological
//          angle | Marketing angle | Why (rationale) | Status
//
// Controls: filter chips by funnel_stage and status. A secondary "By Funnel"
// grouping toggle regroups the same table; "By Category" is the default.
//
// Structured path: bespoke React + useApi(). No markdown renderer. The
// architectureT palette matches the rest of the dashboard.

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type Item = {
  id: string;
  title: string;
  category: string | null;
  category_order: number | null;
  content_type: string | null;
  funnel_stage: string | null;
  status: string | null;
  priority: string | null;
  description: string | null;
  sort_order: number | null;
  psychological_angle: string | null;
  marketing_angle: string | null;
  rationale: string | null;
  created_at: string;
};

type ApiResponse = { items: Item[] };

type ViewMode = "category" | "funnel";

const FUNNEL_ORDER = ["TOFU", "MOFU", "BOFU"];

// ── Tag styling ──────────────────────────────────────────────────────────────

function Tag({
  label,
  fg,
  bg,
  border,
}: {
  label: string;
  fg: string;
  bg: string;
  border: string;
}) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: fg,
        background: bg,
        border: `1px solid ${border}`,
        padding: "2px 8px",
        borderRadius: 6,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {label}
    </span>
  );
}

function ContentTypeTag({ type }: { type: string | null }) {
  if (!type) return <Dash />;
  return <Tag label={type} fg={T.skyDark} bg={T.skySoft} border={T.sky} />;
}

// Funnel stage gets a distinct color per stage so the table reads at a glance.
const FUNNEL_COLORS: Record<string, { fg: string; bg: string; border: string }> = {
  TOFU: { fg: T.green, bg: T.greenSoft, border: T.green },
  MOFU: { fg: T.amber, bg: T.amberSoft, border: T.amber },
  BOFU: { fg: T.plum, bg: T.plumSoft, border: T.plum },
};

function FunnelTag({ stage }: { stage: string | null }) {
  if (!stage) return <Dash />;
  const c = FUNNEL_COLORS[stage] ?? { fg: T.ink2, bg: T.bg2, border: T.line };
  return <Tag label={stage} fg={c.fg} bg={c.bg} border={c.border} />;
}

function StatusTag({ status }: { status: string | null }) {
  if (!status) return <Dash />;
  return <Tag label={status} fg={T.ink2} bg={T.bg2} border={T.line} />;
}

// Subtle dash for empty cells (gracefully renders while the meta columns are
// still being populated by a concurrent agent).
function Dash() {
  return <span style={{ color: T.ink3, fontSize: 13 }}>—</span>;
}

// ── Expandable prose cell ─────────────────────────────────────────────────────
// The three meta columns can be long. We show a readable clamp (4 lines) and
// a tiny More/Less toggle that expands in place. Empty → subtle dash.

function MetaCell({ text }: { text: string | null }) {
  const [open, setOpen] = useState(false);
  const content = text?.trim();
  if (!content) return <Dash />;

  return (
    <div>
      <p
        style={{
          fontSize: 12.5,
          color: T.ink2,
          lineHeight: 1.5,
          margin: 0,
          ...(open
            ? { whiteSpace: "pre-wrap" }
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {content}
      </p>
      {content.length > 130 && (
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: 3,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: T.accent,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {open ? "Less" : "More"}
        </button>
      )}
    </div>
  );
}

// ── Filter chip ──────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "5px 12px",
        borderRadius: 999,
        cursor: "pointer",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        border: `1px solid ${active ? T.accent : T.line}`,
        background: active ? T.accent : T.white,
        color: active ? T.white : T.ink2,
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );
}

// ── Table column geometry ─────────────────────────────────────────────────────
// The three meta columns are the point — they get the room.
const COLS: { key: string; label: string; width: string }[] = [
  { key: "title", label: "Title", width: "20%" },
  { key: "type", label: "Type", width: "7%" },
  { key: "funnel", label: "Funnel", width: "7%" },
  { key: "psy", label: "Psychological angle", width: "20%" },
  { key: "mkt", label: "Marketing angle", width: "20%" },
  { key: "why", label: "Why (rationale)", width: "18%" },
  { key: "status", label: "Status", width: "8%" },
];

const cellBase: React.CSSProperties = {
  padding: "12px 14px",
  verticalAlign: "top",
  borderBottom: `1px solid ${T.line}`,
  textAlign: "start",
};

// ── Section header band (one per category / funnel group) ─────────────────────

function SectionBand({ title, count, accent }: { title: string; count: number; accent: string }) {
  return (
    <tr>
      <td
        colSpan={COLS.length}
        style={{
          padding: "10px 14px",
          background: T.bg2,
          borderTop: `2px solid ${accent}`,
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: T.ink,
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, marginInlineStart: 10 }}>
          {count} {count === 1 ? "item" : "items"}
        </span>
      </td>
    </tr>
  );
}

// ── Data row ──────────────────────────────────────────────────────────────────

function DataRow({ item, zebra }: { item: Item; zebra: boolean }) {
  return (
    <tr style={{ background: zebra ? T.bg : T.white }}>
      {/* Title + description sub-line */}
      <td style={cellBase}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.4,
            letterSpacing: "-0.005em",
          }}
        >
          {item.title}
        </div>
        {item.description?.trim() && (
          <div
            style={{
              fontSize: 11.5,
              color: T.ink3,
              lineHeight: 1.45,
              marginTop: 3,
            }}
          >
            {item.description}
          </div>
        )}
      </td>

      <td style={cellBase}>
        <ContentTypeTag type={item.content_type} />
      </td>

      <td style={cellBase}>
        <FunnelTag stage={item.funnel_stage} />
      </td>

      <td style={cellBase}>
        <MetaCell text={item.psychological_angle} />
      </td>

      <td style={cellBase}>
        <MetaCell text={item.marketing_angle} />
      </td>

      <td style={cellBase}>
        <MetaCell text={item.rationale} />
      </td>

      <td style={cellBase}>
        <StatusTag status={item.status} />
      </td>
    </tr>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ContentPlanPage() {
  const api = useApi();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("category");
  const [funnelFilter, setFunnelFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/content-plan");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ApiResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allItems = data?.items ?? [];

  const funnelValues = useMemo(() => {
    const present = new Set(allItems.map((i) => i.funnel_stage).filter(Boolean) as string[]);
    const ordered = FUNNEL_ORDER.filter((f) => present.has(f));
    const extras = [...present].filter((f) => !FUNNEL_ORDER.includes(f)).sort();
    return [...ordered, ...extras];
  }, [allItems]);

  const statusValues = useMemo(
    () => [...new Set(allItems.map((i) => i.status).filter(Boolean) as string[])].sort(),
    [allItems],
  );

  const filtered = useMemo(
    () =>
      allItems.filter((i) => {
        if (funnelFilter.size && !(i.funnel_stage && funnelFilter.has(i.funnel_stage)))
          return false;
        if (statusFilter.size && !(i.status && statusFilter.has(i.status))) return false;
        return true;
      }),
    [allItems, funnelFilter, statusFilter],
  );

  // Group into ordered section bands per the active view.
  const sections = useMemo(() => {
    if (view === "funnel") {
      const groups = new Map<string, Item[]>();
      for (const i of filtered) {
        const key = i.funnel_stage ?? "Unstaged";
        const arr = groups.get(key) ?? [];
        arr.push(i);
        groups.set(key, arr);
      }
      const keys = [
        ...FUNNEL_ORDER.filter((k) => groups.has(k)),
        ...[...groups.keys()].filter((k) => !FUNNEL_ORDER.includes(k)).sort(),
      ];
      return keys.map((key) => ({
        title: key,
        accent: FUNNEL_COLORS[key]?.border ?? T.skyDark,
        items: groups.get(key)!,
      }));
    }
    // By category — preserve category_order, then sort_order within.
    const groups = new Map<string, { order: number; items: Item[] }>();
    for (const i of filtered) {
      const key = i.category ?? "Uncategorized";
      const g = groups.get(key) ?? { order: i.category_order ?? 999, items: [] };
      g.items.push(i);
      groups.set(key, g);
    }
    return [...groups.entries()]
      .sort((a, b) => a[1].order - b[1].order)
      .map(([title, g]) => ({
        title,
        accent: T.skyDark,
        items: g.items.sort((x, y) => (x.sort_order ?? 999) - (y.sort_order ?? 999)),
      }));
  }, [filtered, view]);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, val: string) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setter(next);
  };

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        boxSizing: "border-box",
        padding: "36px 40px 80px",
      }}
    >
      {children}
    </div>
  );

  if (error) {
    return shell(
      <div
        style={{
          padding: "16px 20px",
          color: T.red,
          background: T.redSoft,
          border: `1px solid ${T.red}`,
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        Failed to load content plan: {error}
      </div>,
    );
  }

  if (data === null) {
    return shell(
      <div
        style={{
          padding: 24,
          fontSize: 14,
          color: T.ink3,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
        Loading…
      </div>,
    );
  }

  return shell(
    <>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: T.skyDark,
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          MyJarvis · Marketing
        </div>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: T.ink,
            margin: "0 0 6px",
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
          }}
        >
          Content Plan
        </h1>
      </div>

      {/* Legend — How to read this plan */}
      <div
        style={{
          background: T.white,
          border: `1px solid ${T.line}`,
          borderInlineStart: `4px solid ${T.skyDark}`,
          borderRadius: 10,
          padding: "16px 20px",
          marginBottom: 24,
          maxWidth: 980,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: T.skyDark,
            marginBottom: 8,
          }}
        >
          How to read this plan
        </div>
        <p style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.65, margin: 0 }}>
          We&rsquo;re in strategy. Each row is a candidate content piece with its reasoning &mdash;
          the <strong>psychological angle</strong> (why it works on the reader) and the{" "}
          <strong>marketing angle</strong> (its role in the funnel). We lead with our signature pair
          (<em>The 9am Call Sheet</em> + <em>From a 40,000-Company List to a Monday Call Sheet</em>);
          the universal use-cases attract top-of-funnel; &ldquo;Fix the plumbing first&rdquo; bridges
          content into a paid discovery engagement.
        </p>
      </div>

      {/* Controls — grouping toggle + filters */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "flex-end",
          marginBottom: 18,
        }}
      >
        {/* Grouping toggle */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={controlLabel}>Group by</div>
          <div
            style={{
              display: "inline-flex",
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {(
              [
                ["category", "By Category"],
                ["funnel", "By Funnel"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setView(mode)}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 14px",
                  cursor: "pointer",
                  border: "none",
                  background: view === mode ? T.accent : T.white,
                  color: view === mode ? T.white : T.ink2,
                  transition: "all 0.12s",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Funnel filter */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={controlLabel}>Funnel stage</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {funnelValues.map((f) => (
              <FilterChip
                key={f}
                label={f}
                active={funnelFilter.has(f)}
                onClick={() => toggle(funnelFilter, setFunnelFilter, f)}
              />
            ))}
          </div>
        </div>

        {/* Status filter */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={controlLabel}>Status</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {statusValues.map((s) => (
              <FilterChip
                key={s}
                label={s}
                active={statusFilter.has(s)}
                onClick={() => toggle(statusFilter, setStatusFilter, s)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* The table */}
      {filtered.length === 0 ? (
        <div style={{ padding: 24, fontSize: 14, color: T.ink3, textAlign: "center" }}>
          No items match the current filters.
        </div>
      ) : (
        <div
          style={{
            border: `1px solid ${T.line}`,
            borderRadius: 12,
            overflow: "hidden",
            background: T.white,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <colgroup>
              {COLS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      background: T.white,
                      color: T.ink3,
                      fontWeight: 700,
                      fontSize: 10.5,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "11px 14px",
                      textAlign: "start",
                      borderBottom: `2px solid ${T.line}`,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <SectionRows key={`${view}-${s.title}`} section={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>,
  );
}

const controlLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: T.ink3,
};

// One category/funnel band followed by its zebra-striped rows.
function SectionRows({
  section,
}: {
  section: { title: string; accent: string; items: Item[] };
}) {
  return (
    <>
      <SectionBand title={section.title} count={section.items.length} accent={section.accent} />
      {section.items.map((item, idx) => (
        <DataRow key={item.id} item={item} zebra={idx % 2 === 1} />
      ))}
    </>
  );
}

(ContentPlanPage as unknown as { path: string }).path = "/content-plan";
